import { useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LineArrowDownIcon } from 'src/app/_components/icons/line/LineArrowDownIcon';
import KottonseedButton from 'src/app/(default)/_components/_common/Button';
import KottonseedInput from 'src/app/(default)/_components/_common/Input';
import KottonseedLabel from 'src/app/(default)/_components/_common/Label';
import KottonseedSelect from 'src/app/(default)/_components/_common/Select';
import { isListLockableQnaCategory } from 'src/app/(default)/support/qna/_utils/listLockableQnaCategory';
import { resolveLockedQnaCategory } from 'src/app/(default)/support/qna/_utils/resolveLockedQnaCategory';
import type { QnAFormData } from 'src/app/(default)/support/qna/write/_components';

import { trackEvent } from '@/libs/analytics/events';
import { useCommonCodeQuery } from '@/store/server/common/common/common.query';
import { useWriteInquiryMutation } from '@/store/server/common/support/support.query';

import AnswerNoticeSection from './AnswerNoticeSection';
import FileUploadSection from './FileUploadSection';
import QuestionSection from './QuestionSection';

interface QnaFormProps {
  // 쿼리(category)로 받은 문자열. 이 문자열을 포함하는 문의유형이 있으면 고정 선택(변경불가).
  initialCategory?: string;
}

const QnaForm = ({ initialCategory }: QnaFormProps) => {
  const router = useRouter();
  const {
    control,
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useFormContext<QnAFormData>();

  const { data: commonCodeData } = useCommonCodeQuery({
    groupId: 'QC',
  });

  // category 쿼리를 dtl_cd_nm 에 포함하는 문의유형(예: "설레임" → "설레임런"). 매칭되면 고정 선택.
  // 매칭 없으면 일반 선택으로 fallback. 매칭 규칙은 resolveLockedQnaCategory 참고.
  const lockedCategory = useMemo(
    () => resolveLockedQnaCategory(commonCodeData?.rows, initialCategory),
    [initialCategory, commonCodeData],
  );

  // 매칭된 유형을 폼 값으로 고정(제출 시 포함). 매칭 없으면 기존과 동일하게 사용자가 선택.
  useEffect(() => {
    if (lockedCategory) setValue('category', lockedCategory.code, { shouldDirty: true, shouldValidate: true });
  }, [lockedCategory, setValue]);

  const { mutate: writeInquiryMutate, isPending: writeInquiryLoading } = useWriteInquiryMutation({
    onSuccess: () => {
      trackEvent({ name: 'click_cta', params: { cta_name: 'submit_qna' } });
      // 목록 고정 허용 유형(챔피언스 런)으로 작성했으면 목록도 같은 유형으로 고정(categoryName)해 돌아간다.
      // 설레임런 등 그 외 유형은 기존처럼 일반 목록으로.
      router.push(
        lockedCategory && isListLockableQnaCategory(initialCategory)
          ? `/support/qna/list?categoryName=${encodeURIComponent(initialCategory)}`
          : '/support/qna/list',
      );
    },
    onError: (error) => {
      console.error('error', error);
    },
  });

  const subjectRegister = register('subject', {
    required: '제목을 입력해주세요',
  });

  const watchedCategory = watch('category');
  const watchedSubject = watch('subject');
  const watchedContents = watch('contents');

  const isValidForm = !!watchedCategory && !!watchedSubject && !!watchedContents;

  const onSubmit = (data: QnAFormData) => {
    writeInquiryMutate({
      category: data.category,
      subject: data.subject,
      contents: data.contents,
      pushSms: data.pushSms,
      pushEmail: data.pushEmail,
      bbsFile: data.bbsFile,
    });
  };

  return (
    <form className="flex w-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-full flex-col gap-6">
        <Controller
          name="category"
          control={control}
          rules={{ required: '문의유형을 선택해주세요' }}
          render={({ field, fieldState }) =>
            lockedCategory ? (
              // category 쿼리로 매칭된 문의유형 — 읽기 전용 고정(변경불가). (disabled Select 는 값 라벨 표기 불가)
              <div className="flex flex-1 flex-col gap-2">
                <KottonseedLabel id="qna-category-label" className="!typo-body-2">
                  문의유형
                </KottonseedLabel>
                <div
                  role="group"
                  aria-labelledby="qna-category-label"
                  className="flex h-11 items-center justify-between rounded-lg border border-g-20 bg-g-30 px-5 text-base text-g-60"
                >
                  <span>{lockedCategory.name}</span>
                  <span aria-hidden>
                    <LineArrowDownIcon size={16} color="var(--color-g-70)" />
                  </span>
                </div>
              </div>
            ) : (
              <>
                <KottonseedSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  label="문의유형"
                  placeholder="문의유형을 선택해주세요"
                  options={
                    commonCodeData?.rows
                      .map((code) => ({
                        label: code.name,
                        value: code.code,
                      }))
                      .filter((code) => code.label !== 'NFT') ?? []
                  }
                  variant="square_fill"
                />
                {fieldState.error && (
                  <p className="pt-2 typo-caption-1 whitespace-pre-line text-rd-50">{fieldState.error.message}</p>
                )}
              </>
            )
          }
        />
      </div>

      <div className="w-full pt-6">
        <KottonseedInput
          label="제목"
          placeholder="제목을 입력해주세요"
          {...subjectRegister}
          maxLength={105}
          errorMessage={errors.subject?.message}
        />
      </div>

      <div className="w-full pt-6">
        <QuestionSection />
      </div>

      <div className="w-full pt-6">
        <FileUploadSection />
      </div>

      {/* Divider */}
      <div className="mt-5 h-[1px] w-full bg-g-20" />

      <div className="w-full pt-6">
        <AnswerNoticeSection />
      </div>

      <div className="w-full pt-10">
        <KottonseedButton
          size="large"
          className="w-full"
          type="submit"
          disabled={!isValidForm}
          isLoading={writeInquiryLoading}
        >
          문의하기
        </KottonseedButton>
      </div>
    </form>
  );
};

export default QnaForm;
